/**
 * Capacity Table Tab Component for RNCM Productivity
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target, Plus, CalendarPlus, Trash2, Pencil, Calendar } from 'lucide-react';
import { TeamBadge, OutcomeBadge } from './Badges';
import { formatMedicareWeek } from './constants';

export const CapacityTab = ({
  capacityData,
  canEdit,
  onAddCapacity,
  onNewWeek,
  onBulkDelete,
  onEdit,
  onDelete,
  initialPeriod = '',
  onPeriodChange
}) => {
  // Extract unique periods from capacity data
  const periods = useMemo(() => {
    const periodMap = new Map();
    capacityData.forEach(c => {
      const key = c.period_start;
      if (key && !periodMap.has(key)) {
        periodMap.set(key, {
          period_start: c.period_start,
          period_end: c.period_end || c.period_start,
          count: 0
        });
      }
      if (key) {
        periodMap.get(key).count++;
      }
    });
    return Array.from(periodMap.values()).sort((a, b) => b.period_start.localeCompare(a.period_start));
  }, [capacityData]);

  // Use initialPeriod if provided, otherwise default to most recent
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    if (initialPeriod) return initialPeriod;
    if (periods.length > 0) return periods[0].period_start;
    return 'all';
  });
  
  // Update when initialPeriod changes (e.g., after creating new week)
  React.useEffect(() => {
    if (initialPeriod && initialPeriod !== selectedPeriod) {
      setSelectedPeriod(initialPeriod);
    }
  }, [initialPeriod]);
  
  // Notify parent of period changes
  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);
    if (onPeriodChange) onPeriodChange(value);
  };
  
  // Filter capacity data by selected period
  const filteredData = useMemo(() => {
    if (selectedPeriod === 'all') return capacityData;
    return capacityData.filter(c => c.period_start === selectedPeriod);
  }, [capacityData, selectedPeriod]);

  // Calculate summary stats for selected period
  const periodStats = useMemo(() => {
    if (filteredData.length === 0) return null;
    const totalCapacity = filteredData.reduce((sum, c) => sum + (c.visit_capacity || 0), 0);
    const overCapacity = filteredData.filter(c => c.planning_outcome === 'Over Capacity').length;
    const withinThreshold = filteredData.filter(c => c.planning_outcome === 'Within Threshold').length;
    return { totalCapacity, overCapacity, withinThreshold, avgCapacity: (totalCapacity / filteredData.length).toFixed(1) };
  }, [filteredData]);

  return (
    <div className="space-y-4">
      {/* Futuristic Header Card */}
      <div className="relative overflow-hidden rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-cyan-500/30 p-6" style={{ boxShadow: '0 0 30px rgba(34, 211, 238, 0.15)' }}>
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-xl">
                <Target className="h-5 w-5 text-cyan-400" style={{ filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.6))' }} />
              </div>
              RNCM Visit Capacity
            </h3>
            <p className="text-slate-400 mt-1 text-sm">Planning capacity vs expectations</p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {/* Period Selector */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-cyan-400" />
              <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-[220px] bg-slate-800/50 border-slate-600 text-white">
                  <SelectValue placeholder="Select Medicare Week" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Periods ({capacityData.length} total)</SelectItem>
                  {periods.map(p => (
                    <SelectItem key={p.period_start} value={p.period_start}>
                      {formatMedicareWeek(p.period_start, p.period_end)} ({p.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {canEdit && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={onNewWeek} className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400">
                  <CalendarPlus className="h-4 w-4 mr-1" /> New Week
                </Button>
                <Button size="sm" onClick={onAddCapacity} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white border-0 shadow-lg shadow-cyan-500/30">
                  <Plus className="h-4 w-4 mr-1" /> Add Capacity
                </Button>
                {filteredData.length > 0 && (
                  <Button size="sm" variant="destructive" onClick={onBulkDelete} className="bg-gradient-to-r from-rose-500 to-red-600 border-0 shadow-lg shadow-rose-500/30">
                    <Trash2 className="h-4 w-4 mr-1" /> Delete Period
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Period Display Banner with Stats */}
        {selectedPeriod !== 'all' && periodStats && (
          <div className="mt-6 p-4 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-xl border border-cyan-500/20">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm font-medium text-cyan-400">
                  Medicare Week: {formatMedicareWeek(
                    periods.find(p => p.period_start === selectedPeriod)?.period_start,
                    periods.find(p => p.period_start === selectedPeriod)?.period_end
                  )}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {filteredData.length} staff entries
                </p>
              </div>
              <div className="flex gap-8 text-sm">
                <div className="text-center">
                  <p className="font-bold text-2xl text-white" style={{ textShadow: '0 0 15px rgba(255,255,255,0.3)' }}>{periodStats.avgCapacity}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Avg Capacity</p>
                </div>
                <div className="text-center">
                  <p className="font-bold text-2xl text-emerald-400" style={{ textShadow: '0 0 15px rgba(52, 211, 153, 0.5)' }}>{periodStats.withinThreshold}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Within Threshold</p>
                </div>
                <div className="text-center">
                  <p className="font-bold text-2xl text-rose-400" style={{ textShadow: '0 0 15px rgba(251, 113, 133, 0.5)' }}>{periodStats.overCapacity}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Over Capacity</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Futuristic Table */}
      <div className="relative overflow-hidden rounded-2xl bg-slate-900/60 backdrop-blur-xl border border-slate-700/50" style={{ boxShadow: '0 0 20px rgba(34, 211, 238, 0.1)' }}>
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-transparent" />
        <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-purple-500/20 to-transparent" />
        
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-700/50 bg-slate-800/30">
              <TableHead className="text-cyan-400 font-semibold">Clinician</TableHead>
              <TableHead className="text-cyan-400 font-semibold">Hire Date</TableHead>
              <TableHead className="text-cyan-400 font-semibold">Team</TableHead>
              <TableHead className="text-cyan-400 font-semibold">Visit Capacity</TableHead>
              <TableHead className="text-cyan-400 font-semibold">Expectation</TableHead>
              <TableHead className="text-cyan-400 font-semibold">Planning Outcome</TableHead>
              <TableHead className="text-cyan-400 font-semibold">Comment</TableHead>
              <TableHead className="text-cyan-400 font-semibold text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-400">
                  <div className="flex flex-col items-center gap-2">
                    <Target className="h-12 w-12 text-slate-600" />
                    <p>{selectedPeriod === 'all' 
                      ? 'No capacity data yet. Add entries or upload a file.'
                      : 'No entries for this Medicare week. Use "New Week" to create a new week with staff entries.'}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((c, idx) => (
                <TableRow key={c.id || idx} className="border-b border-slate-700/30 hover:bg-slate-800/30 transition-colors">
                  <TableCell className="font-medium text-white">{c.clinician_name}</TableCell>
                  <TableCell className="text-slate-300">{c.hire_date || '—'}</TableCell>
                  <TableCell><TeamBadge team={c.team} /></TableCell>
                  <TableCell className="font-bold text-white text-lg">{c.visit_capacity}</TableCell>
                  <TableCell className="text-slate-300">{c.expectation_min}-{c.expectation_max}</TableCell>
                  <TableCell><OutcomeBadge outcome={c.planning_outcome} /></TableCell>
                  <TableCell className="max-w-[200px] truncate text-slate-400">{c.comment || '—'}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex gap-1 justify-center">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                        onClick={() => onEdit(c)}
                        data-testid={`edit-capacity-${c.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                        onClick={() => onDelete(c)}
                        data-testid={`delete-capacity-${c.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CapacityTab;
